import type { ComponentClass } from 'react';

import type Step from 'components/core/createModify/interfaces/step';
import type { StepComponentResolver, StepFieldsResolver } from 'components/core/createModify/interfaces/step';
import type { StepFields } from 'components/core/createModify/interfaces/stepFields';
import type Tier from 'components/core/createModify/interfaces/tier';

import { defaultTranslateFields, translateFields } from 'utils/intlUtils';

import type { StepComponentProps } from '../stepFields/StepComponentCore';

/**
 * Type guard to check if the step property is a resolver function.
 * Class components have a prototype with render method, resolver functions do not.
 */
const isStepResolver = <
  TData extends Record<string, any>,
  TMetadata extends Record<string, any>,
  TSeededData extends Record<string, any>,
>(
  step:
    | ComponentClass<StepComponentProps<TData, TMetadata, TSeededData>>
    | StepComponentResolver<TData, TMetadata, TSeededData>
    | undefined
): step is StepComponentResolver<TData, TMetadata, TSeededData> =>
  typeof step === 'function' && !step.prototype?.render;

export const isFieldsResolver = <
  TData extends Record<string, any>,
  TMetadata extends Record<string, any>,
  TSeededData extends Record<string, any>,
>(
  fields: StepFields<TData, TMetadata, TSeededData> | StepFieldsResolver<TData, TMetadata, TSeededData> | undefined
): fields is StepFieldsResolver<TData, TMetadata, TSeededData> => typeof fields === 'function';

const builderTranslateFields = [...defaultTranslateFields, 'tooltipContents'];

const translatedFieldsCache = new WeakMap<StepFields<any, any, any>, StepFields<any, any, any>>();

const getTranslatedFields = (fields: StepFields<any, any, any>): StepFields<any, any, any> => {
  const cached = translatedFieldsCache.get(fields);
  if (cached) {
    return cached;
  }

  const translated = translateFields(fields, builderTranslateFields) as StepFields<any, any, any>;
  translatedFieldsCache.set(fields, translated);

  return translated;
};

export const resolveStepComponent = <
  TData extends Record<string, any>,
  TMetadata extends Record<string, any>,
  TSeededData extends Record<string, any>,
>(
  activeStep: Step<TData, TMetadata, TSeededData> | undefined,
  tier: Tier<TData, TMetadata, TSeededData>
): ComponentClass<StepComponentProps<TData, TMetadata, TSeededData>> | null | undefined => {
  if (!activeStep) {
    return null;
  }

  if (isStepResolver(activeStep.step)) {
    return activeStep.step({ tier });
  }

  return activeStep.step;
};

/**
 * Resolves the fields for the active step.
 * If `fields` is a resolver function, it will be called to dynamically resolve the fields.
 * Otherwise, returns the static `fields` property.
 *
 * Note: When fields are dynamically resolved via a resolver function, they are wrapped with `translateFields`
 * to ensure labels, placeholders, and tooltips are translated. Static fields don't need this as they
 * are already translated via the `Builders` config in `builders.ts`.
 *
 * @param activeStep - The current active step configuration
 * @param tier - The current tier data used for dynamic resolution
 * @returns The resolved fields, or undefined if no active step
 */
export const resolveStepFields = <
  TData extends Record<string, any>,
  TMetadata extends Record<string, any>,
  TSeededData extends Record<string, any>,
>(
  activeStep: Step<TData, TMetadata, TSeededData> | undefined,
  tier: Tier<TData, TMetadata, TSeededData>
): StepFields<TData, TMetadata, TSeededData> | undefined => {
  if (!activeStep) {
    return undefined;
  }

  if (isFieldsResolver(activeStep.fields)) {
    const dynamicFields = activeStep.fields({ tier });

    /*
     * Translate dynamically resolved fields (static fields are already translated via Builders config).
     * Casts are required because:
     * 1. Input cast: WeakMap is typed with `any` and cannot preserve generic type parameters per call
     * 2. Output cast: WeakMap.get() returns `StepFields<any, any, any>`, not the caller's generic type
     */
    return dynamicFields
      ? (getTranslatedFields(dynamicFields) as StepFields<TData, TMetadata, TSeededData>)
      : undefined;
  }

  return activeStep.fields;
};

export const createResolvedActiveStep = <
  TData extends Record<string, any>,
  TMetadata extends Record<string, any>,
  TSeededData extends Record<string, any>,
>(
  activeStep: Step<TData, TMetadata, TSeededData> | undefined,
  resolvedFields: StepFields<TData, TMetadata, TSeededData> | undefined
): Step<TData, TMetadata, TSeededData> | undefined => {
  if (!activeStep) {
    return undefined;
  }

  if (resolvedFields && resolvedFields !== activeStep.fields) {
    return { ...activeStep, fields: resolvedFields };
  }

  return activeStep;
};

export const createResolvedTier = <
  TData extends Record<string, any>,
  TMetadata extends Record<string, any>,
  TSeededData extends Record<string, any>,
>(
  tier: Tier<TData, TMetadata, TSeededData>,
  activeStep: Step<TData, TMetadata, TSeededData> | undefined,
  resolvedActiveStep: Step<TData, TMetadata, TSeededData> | undefined
): Tier<TData, TMetadata, TSeededData> => {
  if (!resolvedActiveStep || resolvedActiveStep === activeStep) {
    return tier;
  }

  return { ...tier, activeStep: resolvedActiveStep };
};
